import { mergeAttributes } from '@tiptap/core'
import CodeBlock from '@tiptap/extension-code-block'

import type { Node as ProseMirrorNode } from '@tiptap/pm/model'

/** Default language attr for gutter-inserted blocks (shown as "Text" in the header). */
export const ANNOUNCEMENT_CODE_BLOCK_DEFAULT_LANGUAGE = 'text'

const LANGUAGE_LABELS: Record<string, string> = {
  text: 'Text',
  plaintext: 'Plain Text',
  javascript: 'JavaScript',
  typescript: 'TypeScript',
  jsx: 'JSX',
  tsx: 'TSX',
  json: 'JSON',
  html: 'HTML',
  css: 'CSS',
  sql: 'SQL',
  python: 'Python',
  java: 'Java',
  c: 'C',
  cpp: 'C++',
  'c++': 'C++',
  go: 'Go',
  rust: 'Rust',
  ruby: 'Ruby',
  php: 'PHP',
  shell: 'Shell',
  bash: 'Bash',
  yaml: 'YAML',
  markdown: 'Markdown',
}

export const formatCodeBlockLanguageLabel = (
  language: string | null | undefined
): string => {
  if (!language) {
    return LANGUAGE_LABELS.text
  }

  const normalized = language.trim().toLowerCase()

  if (LANGUAGE_LABELS[normalized]) {
    return LANGUAGE_LABELS[normalized]
  }

  return normalized
    .split(/[-_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export const AnnouncementCodeBlock = CodeBlock.extend({
  addOptions() {
    return {
      ...this.parent?.(),
      defaultLanguage: ANNOUNCEMENT_CODE_BLOCK_DEFAULT_LANGUAGE,
    }
  },

  parseHTML() {
    return [
      {
        tag: 'pre',
        preserveWhitespace: 'full',
        getAttrs: (element: HTMLElement) => {
          const el = element
          const fromData = el.getAttribute('data-language')

          if (fromData) {
            return { language: fromData }
          }

          const code = el.querySelector('code')
          const className = code?.className ?? ''
          const match = /language-([\w-]+)/.exec(className)

          return { language: match?.[1] ?? null }
        },
      },
    ]
  },

  renderHTML({
    node,
    HTMLAttributes,
  }: {
    node: ProseMirrorNode
    HTMLAttributes: Record<string, string | number | boolean | null | undefined>
  }) {
    const language =
      node.attrs.language ?? ANNOUNCEMENT_CODE_BLOCK_DEFAULT_LANGUAGE
    const label = formatCodeBlockLanguageLabel(language)

    return [
      'pre',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-language': language,
        'data-language-label': label,
      }),
      [
        'code',
        {
          class: language
            ? `${this.options.languageClassPrefix}${language}`
            : null,
        },
        0,
      ],
    ]
  },
})
